import { PartyImage } from '@approvalmax/ui';
import { Button, Grid, Link, SimpleLayout, Text, useLayout } from '@approvalmax/ui/src/components';
import { hooks } from '@approvalmax/utils';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link as RouterLink, Redirect, useHistory } from 'react-router-dom';
import { storageService } from 'services/storage';
import { companyIdQueryParam } from 'shared/constants';
import { getPath, Path } from 'urlBuilder';

import { messages } from './resources/messages';

const Page = () => {
    const history = useHistory();

    useLayout({ containerMinSize: 'xxsmall' });

    const [searchParams] = hooks.useUrlSearchParams();
    const companyId = searchParams.get(companyIdQueryParam);
    const consentId = searchParams.get('consent-id');
    const consentType = searchParams.get('consent-type');
    const errorCode = searchParams.get('error-code');

    const bankAccountLink = `${getPath(Path.bankAccounts, companyId ?? '')}?consent-id=${consentId}`;

    const isAuthenticated = storageService.authenticated();

    useEffect(() => {
        let timerId: ReturnType<typeof setTimeout>;

        if (isAuthenticated) {
            timerId = setTimeout(() => history.push(bankAccountLink), 5 * 1000);
        }

        return () => {
            timerId && clearTimeout(timerId);
        };
    }, [bankAccountLink, history, isAuthenticated]);

    // must go before errorCode check, because it might have it own error
    if (consentType === 'reconnect-account') {
        return <Redirect to={`${getPath(Path.reconnectBankAccountSuccess)}?${searchParams.toString()}`} />;
    }

    if (errorCode?.length) {
        return <Redirect to={`${getPath(Path.connectBankAccountFail)}?${searchParams.toString()}`} />;
    }

    return (
        <SimpleLayout size='small'>
            <Helmet title={messages.title} />

            <SimpleLayout.Content>
                <Grid gap={24} justifyItems='center'>
                    <PartyImage />

                    <Text font='headline' fontSize='small' fontWeight='medium' textAlign='center'>
                        {messages.connectionEstablished}
                    </Text>

                    <Text font='body' fontSize='medium' textAlign='center'>
                        {messages.pleaseProceed}
                    </Text>

                    <Grid gap={12}>
                        {isAuthenticated && (
                            <Text font='body' fontSize='medium' textAlign='center'>
                                {messages.youLlBeRedirected}
                            </Text>
                        )}

                        <Link
                            external
                            font='label'
                            fontSize='medium'
                            textAlign='center'
                            href='https://support.approvalmax.com/portal/en/kb/articles/how-to-setup-a-bank-account-connection-in-approvalmax'
                        >
                            {messages.learnMore}
                        </Link>
                    </Grid>

                    {isAuthenticated && (
                        <Button block as={RouterLink} to={bankAccountLink} color='blue80' size='large'>
                            {messages.matchToXero}
                        </Button>
                    )}
                </Grid>
            </SimpleLayout.Content>
        </SimpleLayout>
    );
};

Page.displayName = 'Page';

export default Page;
